import {
  $computeTableMapSkipCellCheck,
  $getTableNodeFromLexicalNodeOrThrow,
  $isTableCellNode,
  TableCellHeaderStates,
  TableCellNode,
} from '@lexical/table';
import {$setSelection, LexicalEditor} from 'lexical';

/**
 * Toggles row header styling for the entire top row (row 0) of a table
 */
export const toggleTableRowHeaders = (
  editor: LexicalEditor,
  tableCellNode: TableCellNode,
  isHeaderRow: boolean
): void => {
  editor.update(() => {
    const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
    const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
    const topRowCells = new Set<TableCellNode>();

    // Get the current header state from any cell in the top row (row 0)
    const firstTopCell = gridMap[0]?.[0]?.cell;
    const newStyle = firstTopCell
      ? firstTopCell.getHeaderStyles() ^ TableCellHeaderStates.ROW
      : TableCellHeaderStates.ROW;

    // Apply header styling only to the top row (row 0)
    for (let col = 0; col < gridMap[0].length; col++) {
      const mapCell = gridMap[0][col]; // Always row 0 (topmost)
      if (!mapCell?.cell) continue;

      if (!topRowCells.has(mapCell.cell)) {
        topRowCells.add(mapCell.cell);
        mapCell.cell.setHeaderStyles(newStyle, TableCellHeaderStates.ROW);
      }
    }

    // Clear selection to avoid stale node references after transformation
    if (isHeaderRow) {
      $setSelection(null);
    }
  });
};

/**
 * Toggles column header styling for the entire left column (column 0) of a table
 */
export const toggleTableColumnHeaders = (
  editor: LexicalEditor,
  tableCellNode: TableCellNode,
  isHeaderColumn: boolean
): void => {
  editor.update(() => {
    const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
    const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
    const leftColumnCells = new Set<TableCellNode>();

    // Get the current header state from any cell in the left column (column 0)
    const firstLeftCell = gridMap[0]?.[0]?.cell;
    const newStyle = firstLeftCell
      ? firstLeftCell.getHeaderStyles() ^ TableCellHeaderStates.COLUMN
      : TableCellHeaderStates.COLUMN;

    // Apply header styling only to the leftmost column (column 0)
    for (let row = 0; row < gridMap.length; row++) {
      const mapCell = gridMap[row][0]; // Always column 0 (leftmost)
      if (!mapCell?.cell) continue;

      if (!leftColumnCells.has(mapCell.cell)) {
        leftColumnCells.add(mapCell.cell);
        mapCell.cell.setHeaderStyles(newStyle, TableCellHeaderStates.COLUMN);
      }
    }

    // Clear selection to avoid stale node references after transformation
    if (isHeaderColumn) {
      $setSelection(null);
    }
  });
};

/**
 * Checks if a table cell has row header styling
 */
export const hasRowHeaderStyling = (cell: TableCellNode): boolean => {
  return (cell.getHeaderStyles() & TableCellHeaderStates.ROW) === TableCellHeaderStates.ROW;
};

/**
 * Checks if a table cell has column header styling
 */
export const hasColumnHeaderStyling = (cell: TableCellNode): boolean => {
  return (cell.getHeaderStyles() & TableCellHeaderStates.COLUMN) === TableCellHeaderStates.COLUMN;
};

/**
 * Gets the first table cell from a collection of nodes
 */
export const getFirstTableCell = (nodes: any[]): TableCellNode | null => {
  const tableCells = nodes.filter($isTableCellNode);
  return tableCells.length > 0 ? tableCells[0] : null;
};
